'use client'

import React, { useState, useRef, useEffect, useMemo } from 'react'
import type { TestimonialsBlock as TestimonialsBlockProps, Testimonial } from '@/payload-types'
import { BlockContainer } from '@/components/BlockContainer'
import { SubHeading } from '@/blocks/SectionHeader/SubHeading'
import { getSectionHeadingLevels } from '@/blocks/SectionHeader/getSectionHeadingLevels'
import { ArrowButton } from '@/components/ui/arrow-button'
import Image from 'next/image'
import { getMediaUrl } from '@/utilities/getMediaUrl'
import { cn } from '@/utilities/ui'

// Animation duration constant for slide effect (matching nav-cta button)
const ANIMATION_DURATION = 100

// Resolve the company logo URL for a testimonial (null if none)
const getLogoUrl = (testimonial: Testimonial): string | null => {
  if (typeof testimonial.customer !== 'object' || !testimonial.customer?.logo) return null
  return typeof testimonial.customer.logo === 'string'
    ? testimonial.customer.logo
    : getMediaUrl(
        testimonial.customer.logo.url || `/media/${testimonial.customer.logo.filename}`,
        testimonial.customer.logo.updatedAt,
      ) || ''
}

// Resolve the author profile image URL for a testimonial (null if none)
const getProfileUrl = (testimonial: Testimonial): string | null => {
  if (!testimonial.profileImage) return null
  return typeof testimonial.profileImage === 'string'
    ? testimonial.profileImage
    : getMediaUrl(
        testimonial.profileImage.url || `/media/${testimonial.profileImage.filename}`,
        testimonial.profileImage.updatedAt,
      ) || ''
}

const getCompanyName = (testimonial: Testimonial): string =>
  typeof testimonial.customer === 'object' ? testimonial.customer?.companyName ?? '' : ''

/**
 * Static, non-interactive testimonial card.
 *
 * Used as the invisible "sizing layer": every testimonial is rendered stacked in the
 * same CSS grid cell so the browser sizes the carousel to the tallest slide - no JS
 * height measurement required. Image slots and the pager use fixed-size placeholders
 * (matching the live card's dimensions) so heights are exact without loading images
 * again or wiring up handlers.
 */
const TestimonialCardSizer: React.FC<{
  testimonial: Testimonial
  showLogos?: boolean | null
  showPager: boolean
  className?: string
}> = ({ testimonial, showLogos, showPager, className }) => {
  const hasLogo = showLogos && typeof testimonial.customer === 'object' && !!testimonial.customer?.logo

  return (
    <div
      aria-hidden="true"
      className={cn('bg-black rounded-[16px] p-7 w-full flex flex-col justify-between', className)}
    >
      {/* Content wrapper */}
      <div className="flex flex-col gap-10">
        {/* Header - Mobile: logo first, Desktop/Tablet: author left, logo right */}
        <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between md:gap-16">
          {/* Company logo - shows first on mobile */}
          {hasLogo && <div className="md:hidden h-5 w-[108px]" />}

          {/* Author section */}
          <div className="flex items-start gap-3">
            {testimonial.profileImage && <div className="shrink-0 size-[54px] rounded-full" />}
            <div className="flex flex-col gap-1">
              <p className="subheading-medium text-white">{testimonial.name}</p>
              <p className="body-copy-medium text-white">{testimonial.role}</p>
            </div>
          </div>

          {/* Company logo - shows on right for desktop/tablet */}
          {hasLogo && <div className="hidden md:block h-5 w-[108px] shrink-0" />}
        </div>

        {/* Testimonial text */}
        <p className="quote-small text-civo-lime">{testimonial.testimonial}</p>
      </div>

      {/* Pager placeholder. MUST stay in sync with the live pager row in TestimonialsBlock
          below: h-10 = ArrowButton "medium" height (40px), md:mt-8 = the 32px gap above the
          dots. If either changes there, update here too or the grid will reserve the wrong
          height and the card can clip again. */}
      {showPager && <div className="h-10 md:mt-8" />}
    </div>
  )
}

type Props = TestimonialsBlockProps & {
  disableInnerContainer?: boolean
  hasHero?: boolean
  isFirstSectionHeader?: boolean
}

export const TestimonialsBlock: React.FC<Props> = ({
  subHeading,
  heading,
  description,
  testimonials,
  showLogos = true,
  disableInnerContainer,
  hasHero = true,
  isFirstSectionHeader = false,
  paddingTop,
  paddingBottom,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [displayIndex, setDisplayIndex] = useState(0) // The content currently shown
  const [animationPhase, setAnimationPhase] = useState<'idle' | 'exiting' | 'prep-enter' | 'entering'>('idle')
  const touchStartX = useRef<number>(0)
  const touchEndX = useRef<number>(0)
  const testimonialsArray = useMemo(() =>
    Array.isArray(testimonials) ? testimonials.filter(t => typeof t !== 'string') : [],
    [testimonials]
  )
  const showPager = testimonialsArray.length > 1

  const { headingLevel, eyebrowLevel } = getSectionHeadingLevels({ hasHero, isFirstSectionHeader, hasHeading: Boolean(heading) })
  const HeadingTag = headingLevel

  const goToSlide = (index: number) => {
    const newIndex = Math.max(0, Math.min(index, testimonialsArray.length - 1))
    if (newIndex === currentIndex || animationPhase !== 'idle') return

    setCurrentIndex(newIndex)

    // Phase 1: Exit - current content slides up and out
    setAnimationPhase('exiting')

    // After exit completes, swap content and prep for enter
    setTimeout(() => {
      setDisplayIndex(newIndex)
      setAnimationPhase('prep-enter')

      // After a frame, start enter animation (content slides up from below)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setAnimationPhase('entering')
        })
      })
    }, ANIMATION_DURATION)

    // After enter completes, return to idle
    setTimeout(() => {
      setAnimationPhase('idle')
    }, ANIMATION_DURATION * 2 + 50)
  }

  const goToNext = () => {
    const nextIndex = currentIndex < testimonialsArray.length - 1 ? currentIndex + 1 : 0
    goToSlide(nextIndex)
  }

  const goToPrevious = () => {
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : testimonialsArray.length - 1
    goToSlide(prevIndex)
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.targetTouches[0].clientX
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX
  }

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return

    const distance = touchStartX.current - touchEndX.current
    const isLeftSwipe = distance > 50
    const isRightSwipe = distance < -50

    if (isLeftSwipe) {
      goToNext()
    }
    if (isRightSwipe) {
      goToPrevious()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      goToPrevious()
    } else if (e.key === 'ArrowRight') {
      goToNext()
    }
  }

  // Reset indices when testimonials array changes and current index is out of bounds
  useEffect(() => {
    if (testimonialsArray.length === 0) return

    const maxIndex = testimonialsArray.length - 1
    if (currentIndex > maxIndex) {
      setCurrentIndex(maxIndex)
    }
    if (displayIndex > maxIndex) {
      setDisplayIndex(maxIndex)
    }
  }, [testimonialsArray.length, currentIndex, displayIndex])

  return (
    <BlockContainer
      disableInnerContainer={disableInnerContainer}
      paddingTop={paddingTop ?? true}
      paddingBottom={paddingBottom ?? true}
    >
      {/* SubHeading above both containers */}
      {subHeading && <SubHeading text={subHeading} headingLevel={eyebrowLevel} />}

      <div className="flex flex-col lg:flex-row lg:gap-8 lg:h-full mt-7 lg:mt-[72px]">
        {/* Left Container - Header Content */}
        <div className="flex-1 flex flex-col">
          {heading && (
            <div className="display-small lg:display-large">
              <HeadingTag>
                {heading.split('\n').map((line, i) => (
                  <span key={i} className="block">{line}</span>
                ))}
              </HeadingTag>
            </div>
          )}

          {description && (
            <div className="body-medium text-black/70 lg:body-large mt-4 lg:mt-6">
              <p>{description}</p>
            </div>
          )}

        </div>

        {/* Right Container - Testimonials */}
        <div className="mt-6 lg:mt-0 flex-1 flex flex-col lg:h-full">
          {testimonialsArray.length > 0 && (
            // CSS grid-stack: every slide and the live card share a single grid cell, so the
            // browser sizes this box to the tallest slide. No JS height measurement, no
            // hydration-timing race - correct on every refresh and at every breakpoint.
            <div className="grid">
              {/* Sizing layer - all slides, stacked and invisible, establish the height */}
              {testimonialsArray.map((testimonial, index) => (
                <TestimonialCardSizer
                  key={`sizer-${index}`}
                  testimonial={testimonial as Testimonial}
                  showLogos={showLogos}
                  showPager={showPager}
                  className="col-start-1 row-start-1 invisible pointer-events-none"
                />
              ))}

              {/* Foreground - the single animated, interactive slide in the same cell */}
              <div
                className="col-start-1 row-start-1 focus:outline-none"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onKeyDown={handleKeyDown}
                tabIndex={0}
                role="region"
                aria-label="Testimonials carousel"
                aria-live="polite"
              >
                {(() => {
                  const testimonial = testimonialsArray[displayIndex] as Testimonial
                  if (!testimonial) return null

                  // Get animation classes based on phase
                  // idle: translate-y-0 (visible)
                  // exiting: -translate-y-full with transition (slides up and out)
                  // prep-enter: translate-y-full without transition (jumps to below)
                  // entering: translate-y-0 with transition (slides up into position)
                  // Combined slide + fade animation classes
                  const getAnimationClasses = () => {
                    switch (animationPhase) {
                      case 'exiting':
                        return 'transition-all duration-100 ease-in-out motion-reduce:transition-none -translate-y-full opacity-0 motion-reduce:translate-y-0 motion-reduce:opacity-100'
                      case 'prep-enter':
                        return 'translate-y-full opacity-0' // No transition - instant jump
                      case 'entering':
                        return 'transition-all duration-100 ease-in-out motion-reduce:transition-none translate-y-0 opacity-100'
                      default:
                        return 'translate-y-0 opacity-100'
                    }
                  }

                  // Fade-only classes (for profile image) - faster than slide animation for smoother transition
                  const getFadeClasses = () => {
                    switch (animationPhase) {
                      case 'exiting':
                        return 'transition-opacity duration-300 ease-out motion-reduce:transition-none opacity-0 motion-reduce:opacity-100'
                      case 'prep-enter':
                        return 'opacity-0' // No transition - instant
                      case 'entering':
                        return 'transition-opacity duration-300 ease-out motion-reduce:transition-none opacity-100'
                      default:
                        return 'opacity-100'
                    }
                  }

                  const animationClasses = getAnimationClasses()
                  const fadeClasses = getFadeClasses()

                  // Helper to render animated text
                  const renderAnimatedText = (text: string, className: string) => (
                    <div className="relative overflow-hidden">
                      <p className={cn(className, 'block', animationClasses)}>
                        {text}
                      </p>
                    </div>
                  )

                  // Helper to render animated logo
                  const renderAnimatedLogo = (containerClass: string) => (
                    <div className={cn('overflow-hidden h-5', containerClass)}>
                      <div className={animationClasses}>
                        <Image
                          src={logoUrl!}
                          alt={`${companyName} logo`}
                          width={108}
                          height={20}
                          className="h-5 w-auto object-contain"
                        />
                      </div>
                    </div>
                  )

                  const logoUrl = getLogoUrl(testimonial)
                  const profileUrl = getProfileUrl(testimonial)
                  const companyName = getCompanyName(testimonial)

                  return (
                    <div className="bg-black rounded-[16px] p-7 flex flex-col justify-between h-full">
                      {/* Content wrapper */}
                      <div className="flex flex-col gap-10">
                        {/* Header - Mobile: logo first, Desktop/Tablet: author left, logo right */}
                        <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between md:gap-16">
                          {/* Company logo - shows first on mobile */}
                          {showLogos && logoUrl && renderAnimatedLogo('md:hidden')}

                          {/* Author section */}
                          <div className="flex items-start gap-3">
                            {/* Profile image - fade animation */}
                            {profileUrl && (
                              <div className={cn('shrink-0', fadeClasses)}>
                                <Image
                                  src={profileUrl}
                                  alt={`${testimonial.name} profile`}
                                  width={54}
                                  height={54}
                                  className="size-[54px] rounded-full object-cover"
                                />
                              </div>
                            )}
                            <div className="flex flex-col gap-1">
                              {renderAnimatedText(testimonial.name || '', 'subheading-medium text-white')}
                              {renderAnimatedText(testimonial.role || '', 'body-copy-medium text-white')}
                            </div>
                          </div>

                          {/* Company logo - shows on right for desktop/tablet */}
                          {showLogos && logoUrl && renderAnimatedLogo('hidden md:block shrink-0')}
                        </div>

                        {/* Testimonial text - 28px Newsreader Light on all breakpoints */}
                        {renderAnimatedText(
                          testimonial.testimonial || '',
                          'quote-small text-civo-lime'
                        )}
                      </div>

                      {/* Pager section - md:mt-8 adds a 32px gap above the dots on tablet/desktop.
                          Its height (ArrowButton "medium" = h-10) and md:mt-8 must match the
                          placeholder in TestimonialCardSizer, which reserves the grid height. */}
                      {showPager && (
                        <div className="flex items-center justify-between md:mt-8">
                          {/* Dot pagination - hidden on mobile, shown on tablet/desktop */}
                          <div className="hidden md:flex items-center gap-1">
                            {testimonialsArray.map((_, index) => (
                              <button
                                key={index}
                                className={cn(
                                  'rounded-full transition-all duration-200',
                                  index === currentIndex
                                    ? 'size-3 bg-civo-lime'
                                    : 'size-2 bg-white'
                                )}
                                onClick={() => goToSlide(index)}
                                aria-label={`Go to testimonial ${index + 1}`}
                                aria-current={index === currentIndex ? 'true' : 'false'}
                              />
                            ))}
                          </div>

                          {/* Empty spacer on mobile to push arrows to the right */}
                          <div className="md:hidden" />

                          {/* Navigation arrows */}
                          <div className="flex items-center gap-2">
                            <ArrowButton
                              direction="left"
                              backgroundColor="transparent"
                              size="medium"
                              variant="minimal"
                              animated
                              onClick={goToPrevious}
                            />
                            <ArrowButton
                              direction="right"
                              backgroundColor="transparent"
                              size="medium"
                              variant="minimal"
                              animated
                              onClick={goToNext}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })()}
              </div>
            </div>
          )}
        </div>
      </div>
    </BlockContainer>
  )
}
